import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getPatientById,
  getPatientNotes,
} from "../../../services/patientService";
import PrintShell from "../../../components/UI/PrintShell";
import DemographicsPrintSheet from "./DemographicsPrintSheet";
import { loadPatientUiConfig } from "../usePatientTabs";
import {
  PATIENT_NOTE_TYPE_DEMOGRAPHICS,
  extractPatientNotes,
} from "../patientNote.shared";

function extractPatient(patientRes) {
  const payload = patientRes?.result || patientRes?.data || patientRes;
  return payload?.patient || payload || null;
}

/**
 * Opens in its own tab from the Demographics tab.
 * Screen toolbar has Print / Save as PDF; paper output is info-only (no buttons).
 */
const PatientDemographicsPrintPage = () => {
  const { patientId } = useParams();
  const [patient, setPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setError("");

    (async () => {
      try {
        const uiConfig = await loadPatientUiConfig();
        if (cancelled) return;
        if (!uiConfig.enabledTabKeys.includes("demographics")) {
          setError("Demographics is not enabled for this agency");
          setReady(false);
          return;
        }

        const patientRes = await getPatientById(patientId);
        if (cancelled) return;
        const nextPatient = extractPatient(patientRes);
        if (!nextPatient?.id && !nextPatient?.mrn) {
          throw new Error("Patient record was empty");
        }
        setPatient(nextPatient);

        let nextNotes = [];
        try {
          const notesRes = await getPatientNotes(patientId, {
            page: 1,
            limit: 200,
            type: PATIENT_NOTE_TYPE_DEMOGRAPHICS,
          });
          if (!cancelled) {
            nextNotes = extractPatientNotes(notesRes);
          }
        } catch (notesErr) {
          console.warn("Demographics print: notes failed to load", notesErr);
          nextNotes = [];
        }
        if (cancelled) return;
        setNotes(nextNotes);
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load demographics");
          setReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
        <div className="max-w-sm rounded-xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-800">
            Demographics could not be loaded
          </p>
          <p className="mt-1 text-xs text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen animate-pulse bg-slate-100 py-10">
        <div className="mx-auto h-[700px] w-full max-w-4xl rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  const patientName =
    [patient.firstName, patient.middleName, patient.lastName]
      .filter(Boolean)
      .join(" ") || patient.patientName;

  return (
    <PrintShell
      title="Patient Demographics"
      subtitle={`${patientName || "Patient"} · MRN ${patient.mrn || "—"}`}
      ready={ready}
    >
      <DemographicsPrintSheet patient={patient} notes={notes} />
    </PrintShell>
  );
};

export default PatientDemographicsPrintPage;
